import { curveBasis } from '@visx/curve';
import { useParentSize } from '@visx/responsive';
import { Area, LinePath } from '@visx/shape';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent } from 'react';
import { cn } from '../../lib/cn.ts';
import { formatInt } from '../../lib/format.ts';
import { DATA_SLOT_BG } from './PulseChart.tsx';
import type { PulseSeries } from './PulseChart.tsx';

/** A new event for the stream: one particle drifts in at Now and settles into its kind's layer. */
export interface StreamArrival {
  id: string;
  /** Its series, by index. */
  series: number;
}

export interface ActivityStreamProps {
  /** At most 4 to 6 kinds, in stacking order (centre out is not implied: first is on top). */
  series: readonly PulseSeries[];
  /** Oldest first, one bucket a step (a minute), each one count per series, in series order. */
  buckets: readonly (readonly number[])[];
  /**
   * The newest bucket's place on an absolute timeline (the minute number). When it moves on, the
   * stream drifts one step left instead of redrawing (data-viz.md, live charts).
   */
  step: number;
  /** The first bucket with known data (the feed may not reach back over the whole window). */
  from?: number;
  /** The hover heading for bucket i: "4:12 PM". */
  bucketLabel: (i: number) => string;
  /** Under the first and the last bucket: "60m ago", "Now". */
  startLabel: string;
  endLabel: string;
  /** Accessible summary: "Activity in the last hour: 42 events". */
  label: string;
  /** Events that just arrived, newest last. Each new id drifts in once; ids already seen do nothing. */
  arrivals?: readonly StreamArrival[];
  /** Plot height in px. */
  height?: number;
  /** The smallest full thickness of the scale (in events a minute, to the power 0.6), so a quiet hour does not swell one event to the full height. */
  minScale?: number;
  className?: string;
}

/** Each value as the weighted mean of its neighbours (a small triangular kernel), so one event reads as a soft swell. */
export function smoothSeries(values: readonly number[], radius = 2): number[] {
  return values.map((_, i) => {
    let sum = 0;
    let weight = 0;
    for (let j = -radius; j <= radius; j++) {
      const v = values[i + j];
      if (v === undefined) continue;
      const w = radius + 1 - Math.abs(j);
      sum += v * w;
      weight += w;
    }
    return weight ? sum / weight : 0;
  });
}

export interface StreamShape {
  /** Per series, per bucket: the layer's lower and upper edge, around a centre line at 0. */
  layers: Array<Array<[number, number]>>;
  /** The thickest moment: the scale's reach. */
  peak: number;
}

/**
 * The stream's layers: each series smoothed, then stacked symmetrically around a centre line (a
 * silhouette streamgraph), the first series on top. A bucket's thickness grows with its smoothed
 * total to the power 0.6, shared between the kinds in proportion, so a quiet minute still reads and
 * a burst does not flatten the rest of the hour.
 *
 * With `clampLast`, the trailing edge (the minute still filling and the minutes beside it, where the
 * smoothing kernel is cut short) is never thicker than the thickest of the ten minutes before it, so the stream
 * does not end in a wedge that fans past its bands. A burst after a quiet hour (no earlier
 * thickness) still shows.
 */
/** How many minutes before the trailing edge set its height. */
const RECENT = 10;

export function streamLayers(buckets: readonly (readonly number[])[], seriesCount: number, radius = 2, clampLast = false): StreamShape {
  const smoothed = Array.from({ length: seriesCount }, (_, k) => smoothSeries(buckets.map((b) => b[k] ?? 0), radius));
  const layers: Array<Array<[number, number]>> = Array.from({ length: seriesCount }, () => []);
  const totals = buckets.map((_, i) => smoothed.reduce((a, s) => a + (s[i] ?? 0), 0));
  const fulls = totals.map((t) => Math.pow(t, 0.6));
  // The edge the kernel cannot see past: the last radius + 1 minutes.
  const edge = clampLast ? Math.max(0, buckets.length - (radius + 1)) : buckets.length;
  // Held to the bands just before it (the last ten minutes the kernel sees whole), not the hour's
  // peak, so the stream ends level with the river it closes.
  const before = fulls.slice(Math.max(0, edge - RECENT), edge).reduce((a, t) => Math.max(a, t), 0);
  let peak = 0;
  buckets.forEach((_, i) => {
    const total = totals[i]!;
    const thick = i >= edge && before > 0 ? Math.min(fulls[i]!, before) : fulls[i]!;
    const per = total > 0 ? thick / total : 0;
    peak = Math.max(peak, thick);
    let top = thick / 2;
    for (let k = 0; k < seriesCount; k++) {
      const v = (smoothed[k]![i] ?? 0) * per;
      layers[k]!.push([top - v, top]);
      top -= v;
    }
  });
  return { layers, peak };
}

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
/** The width drawn before the first measure. */
const FIRST_WIDTH = 320;
/** Particles in flight at once; past it a new one is dropped, never queued. */
const PARTICLES_MAX = 8;
/** Room at the right edge for the end dots' halo (radius 5) and a hair more. */
const END_INSET = 6;

interface Particle {
  id: string;
  series: number;
  dx: number;
  dy: number;
}

/**
 * The last hour as a living stream: one soft layer per kind of event, stacked around a centre line
 * and smoothed, in the chart palette at low opacity, fading into the past at the left and brightest
 * at Now. Each new event drifts in at the right edge as a small particle and settles into its
 * layer, once. When the minute moves on the stream glides one step left. Hover (or focus and the
 * arrow keys) shows a guide with that minute's counts by kind. Under reduced motion nothing drifts
 * or flies in: the stream simply updates.
 */
export function ActivityStream({ series, buckets, step, from = 0, bucketLabel, startLabel, endLabel, label, arrivals, height = 80, minScale = 1, className }: ActivityStreamProps) {
  const gid = `stream${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const { parentRef, width: measured } = useParentSize({ initialSize: { width: FIRST_WIDTH }, debounceTime: 50 });
  const width = Math.max(48, measured);
  const n = buckets.length;
  const shape = useMemo(() => streamLayers(buckets, series.length, 2, true), [buckets, series.length]);
  const totals = useMemo(() => series.map((_, k) => buckets.reduce((a, b) => a + (b[k] ?? 0), 0)), [buckets, series]);

  // The scale only grows while the stream is on screen, so a quiet minute never makes it jump.
  const reach = useRef(0);
  reach.current = Math.max(reach.current, shape.peak, minScale);
  const pad = 4;
  const half = (height - pad * 2) / 2;
  const mid = height / 2;
  const y = (v: number) => mid - (v / reach.current) * 2 * half;
  // The stream ends a dot's halo short of the right edge, so Now's marks stay inside the plot.
  const plot = Math.max(1, width - END_INSET);
  const dx = n > 1 ? plot / (n - 1) : plot;
  const x = (i: number) => i * dx;

  // When the window moves on, the stream starts where it was (one step right) and glides into place:
  // read during render, so the remounted layer group starts from there (@starting-style).
  const moved = useRef({ step, drift: 0 });
  if (moved.current.step !== step) {
    const by = step - moved.current.step;
    moved.current = { step, drift: by > 0 && by < 4 && !reducedMotion() ? by * dx : 0 };
  }
  const drift = moved.current.drift;

  // New events become particles: once per id, never on first render, dropped over budget.
  const seen = useRef<Set<string> | null>(null);
  const [particles, setParticles] = useState<Particle[]>([]);
  useEffect(() => {
    const list = arrivals ?? [];
    if (!seen.current) {
      seen.current = new Set(list.map((a) => a.id));
      return;
    }
    const fresh = list.filter((a) => !seen.current!.has(a.id));
    for (const a of fresh) seen.current.add(a.id);
    if (!fresh.length || reducedMotion() || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return;
    setParticles((all) => {
      const out = [...all];
      for (const a of fresh) {
        if (out.length >= PARTICLES_MAX) break;
        // A small, varied approach: from 6 to 16 px right of Now, a little above or below its layer.
        const r = hash(a.id);
        out.push({ id: a.id, series: a.series, dx: 6 + (r % 11), dy: ((r >> 5) % 25) - 12 });
      }
      return out;
    });
  }, [arrivals]);

  const [hover, setHover] = useState<number | null>(null);
  const indexAt = (clientX: number, el: Element) => {
    const r = el.getBoundingClientRect();
    return Math.max(0, Math.min(n - 1, Math.round(((clientX - r.left) / Math.max(1, r.width - END_INSET)) * (n - 1))));
  };
  const onMove = (e: PointerEvent<HTMLDivElement>) => setHover(indexAt(e.clientX, e.currentTarget));
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      setHover((h) => Math.max(0, Math.min(n - 1, (h ?? n - 1) + (e.key === 'ArrowLeft' ? -1 : 1))));
    } else if (e.key === 'Escape') setHover(null);
  };

  const last = n - 1;
  const centre = (k: number, i: number) => {
    const [a, b] = shape.layers[k]?.[i] ?? [0, 0];
    return y((a + b) / 2);
  };
  const colour = (k: number) => `var(--data-${series[k]?.slot ?? 1})`;
  const known = (i: number) => i >= from;
  const hovered = hover == null ? null : buckets[hover];

  return (
    <figure className={cn('flex min-w-0 flex-col gap-2', className)}>
      <ul className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1" aria-label="Legend">
        {series.map((s, k) => (
          <li key={s.key} className="flex items-center gap-1.5 text-caption text-fg-subtle">
            <span aria-hidden className={cn('size-2 rounded-pill', DATA_SLOT_BG[s.slot])} />
            {s.label}
            <span className="text-fg-muted num">{formatInt(totals[k] ?? 0)}</span>
          </li>
        ))}
      </ul>
      <div
        ref={parentRef}
        role="group"
        aria-label={`${label}. Use the arrow keys to read each minute.`}
        tabIndex={0}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
        onKeyDown={onKey}
        onBlur={() => setHover(null)}
        className="relative min-w-0 rounded-control"
        style={{ height }}
      >
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label} className="absolute inset-0 overflow-visible">
          <defs>
            {series.map((s, k) => (
              <linearGradient key={s.key} id={`${gid}-${k}`} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor={colour(k)} stopOpacity="0.05" />
                <stop offset="50%" stopColor={colour(k)} stopOpacity="0.2" />
                <stop offset="100%" stopColor={colour(k)} stopOpacity="0.5" />
              </linearGradient>
            ))}
            {series.map((s, k) => (
              <linearGradient key={s.key} id={`${gid}-edge-${k}`} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor={colour(k)} stopOpacity="0" />
                <stop offset="100%" stopColor={colour(k)} stopOpacity="0.7" />
              </linearGradient>
            ))}
            <radialGradient id={`${gid}-now`}>
              <stop offset="0%" stopColor="var(--fg)" stopOpacity="0.08" />
              <stop offset="100%" stopColor="var(--fg)" stopOpacity="0" />
            </radialGradient>
            <clipPath id={`${gid}-clip`}>
              <rect x={0} y={-pad} width={width} height={height + pad * 2} />
            </clipPath>
            <clipPath id={`${gid}-box`}>
              <rect x={0} y={0} width={width} height={height} />
            </clipPath>
          </defs>
          {/* The river bed: the quiet minutes read as a hairline, not as nothing. */}
          <line x1={x(Math.min(from, last))} x2={plot} y1={mid} y2={mid} stroke="var(--border)" strokeWidth={1} />
          <g clipPath={`url(#${gid}-clip)`}>
            <g key={step} className={drift ? 'motion-drift' : undefined} style={drift ? ({ '--drift': `${drift}px` } as CSSProperties) : undefined}>
              {series.map((s, k) => (
                <Area<[number, number]>
                  key={s.key}
                  data={shape.layers[k] ?? []}
                  defined={(_, i) => known(i)}
                  x={(_, i) => x(i)}
                  y0={(d) => y(d[0])}
                  y1={(d) => y(d[1])}
                  curve={curveBasis}
                  fill={`url(#${gid}-${k})`}
                />
              ))}
              {series.map((s, k) => (
                <LinePath<[number, number]>
                  key={s.key}
                  data={shape.layers[k] ?? []}
                  defined={(_, i) => known(i)}
                  x={(_, i) => x(i)}
                  y={(d) => y(d[1])}
                  curve={curveBasis}
                  stroke={`url(#${gid}-edge-${k})`}
                  strokeWidth={1}
                  fill="none"
                />
              ))}
            </g>
          </g>
          {/* Now: a soft light along the leading edge (kept inside the chart box), and each kind's layer marked where it ends. */}
          <ellipse cx={plot} cy={mid} rx={Math.min(40, width / 6)} ry={height / 2} fill={`url(#${gid}-now)`} clipPath={`url(#${gid}-box)`} />
          {series.map((s, k) => {
            const [a, b] = shape.layers[k]?.[last] ?? [0, 0];
            if (b - a <= 0.01) return null;
            return (
              <g key={s.key}>
                <circle cx={plot} cy={centre(k, last)} r={5} fill={colour(k)} opacity={0.16} />
                <circle cx={plot} cy={centre(k, last)} r={2} fill={colour(k)} />
              </g>
            );
          })}
          {particles.map((p) => (
            <circle
              key={p.id}
              className="motion-settle"
              cx={plot}
              cy={centre(p.series, last)}
              r={2.5}
              fill={colour(p.series)}
              style={{ '--from-x': `${p.dx}px`, '--from-y': `${p.dy}px` } as CSSProperties}
              onAnimationEnd={() => setParticles((all) => all.filter((x) => x.id !== p.id))}
            />
          ))}
          {hover != null && (
            <g aria-hidden>
              <line x1={x(hover)} x2={x(hover)} y1={0} y2={height} stroke="var(--fg-muted)" strokeOpacity={0.7} strokeWidth={1} />
              {series.map((s, k) =>
                (hovered?.[k] ?? 0) > 0 ? <circle key={s.key} cx={x(hover)} cy={centre(k, hover)} r={3} fill={colour(k)} stroke="var(--surface)" strokeWidth={1.5} /> : null,
              )}
            </g>
          )}
        </svg>
        {hover != null && (
          <div
            role="status"
            className="pointer-events-none absolute bottom-full z-(--z-tooltip) mb-1 flex min-w-32 flex-col gap-0.5 rounded-control border border-border bg-raised px-2 py-1.5 text-caption shadow-elevation-1"
            style={x(hover) > width / 2 ? { right: width - x(hover) + 8 } : { left: x(hover) + 8 }}
          >
            <span className="font-medium text-fg">{bucketLabel(hover)}</span>
            {!known(hover) ? (
              <span className="text-fg-muted">Not in the feed</span>
            ) : (hovered ?? []).every((v) => !v) ? (
              <span className="text-fg-muted">Nothing happened</span>
            ) : (
              series.map((s, k) =>
                hovered?.[k] ? (
                  <span key={s.key} className="flex items-center gap-1.5 text-fg-muted">
                    <span aria-hidden className={cn('size-2 rounded-pill', DATA_SLOT_BG[s.slot])} />
                    {s.label}
                    <span className="ml-auto pl-3 text-fg num">{formatInt(hovered[k] ?? 0)}</span>
                  </span>
                ) : null,
              )
            )}
          </div>
        )}
      </div>
      <figcaption className="flex justify-between text-caption text-fg-subtle" aria-hidden>
        <span>{startLabel}</span>
        <span>{endLabel}</span>
      </figcaption>
    </figure>
  );
}

/** A small stable hash of an id, for a particle's varied but repeatable path. */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}
